import { Component, OnDestroy, OnInit } from '@angular/core';
import { PersonType } from 'src/app/services/models/enums';
import { finalize, skip, Subscription, take } from 'rxjs';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';
import { MatSelectChange } from '@angular/material/select';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { DateUtilsService } from 'src/app/services/date-utils.service';
import {
  AttributeResumeAttributesResponseIndicatorsInnerDto,
  DataIngestionService,
  ListResumeAttributesRequestParams,
} from 'src/generated-sources/openapi/data-ingestion/data-ingestion';

@Component({
  selector: 'app-attributes-report',
  templateUrl: './attributes-report.component.html',
  styleUrls: ['./attributes-report.component.scss'],
  standalone: false,
})
export class AttributesReportComponent implements OnInit, OnDestroy {
  indicators: AttributeResumeAttributesResponseIndicatorsInnerDto[] = [];
  selectedWallet: WalletResponseDto;
  urlAssets: string = this.localStorageService.getItem('urlAssets');
  lastProcessingDate = '- - / - - / - - - -';
  isLoadingAttributes = true;
  hasError = false;

  personType = PersonType.PERSON;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly globalService: GlobalServiceService,
    private readonly walletDataService: WalletDataService,
    private readonly localStorageService: LocalStorageService,
    private readonly dataIngestionService: DataIngestionService,
    private readonly snackbarService: SnackbarService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly indicatorDataService: IndicatorDataService,
  ) {}

  ngOnInit() {
    this.personType = this.globalService.getPersonTypeCurrentValue();

    this.subscriptions.push(
      this.globalService
        .getPersonType$()
        .pipe(skip(1))
        .subscribe((type) => {
          this.personType = type;
          if (this.selectedWallet) {
            this.fetchData();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onWalletSelect(event: MatSelectChange) {
    this.selectedWallet = event.value;
    this.fetchData();
  }

  fetchData() {
    if (!this.selectedWallet) {
      return;
    }

    this.isLoadingAttributes = true;
    this.hasError = false;
    const reportParams: ListResumeAttributesRequestParams = {
      walletId: this.selectedWallet.id,
    };

    this.dataIngestionService
      .listResumeAttributes(reportParams)
      .pipe(finalize(() => (this.isLoadingAttributes = false)))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.hasError = false;
          this.indicators = this.indicatorDataService.sortByDisplayOrder(
            response?.indicators || [],
            (item) => item.id ?? '',
          );
          this.lastProcessingDate = response?.indicators.length
            ? this.dateUtilsService.formatDate(response.processingDate)
            : $localize`:@@js_unavailable:indisponible`;
          this.fillBarChart();
        },
        error: (error) => {
          this.hasError = true;
          this.indicators = [];
          this.lastProcessingDate = $localize`:@@js_unavailable:indisponible`;
          this.snackbarService.error(
            $localize`:@@js_getIndicatorsResumeListError:Error al intentar obtener el resumen de indicadores`,
          );
        },
      });
  }

  fillBarChart() {
    if (this.indicators) {
      const chartLabels = [];
      let barSV = [];
      let barP = [];
      let barN = [];

      this.indicators.forEach((indicator) => {
        chartLabels.push(indicator.name);
        barSV.push(indicator.noVariation);
        barP.push(indicator.positiveVariation);
        barN.push(indicator.negativeVariation);
      });

      this.globalService.chartAll$.emit({
        change: true,
        data: barSV,
        dataP: barP,
        dataN: barN,
        labels: chartLabels,
        label: $localize`Resumen de indicadores`,
        type: 'bar',
        indexAxis: 'y',
      });
    }
  }
}
